/**
 * @file: useChatSendPipeline.ts
 *
 * The conversation's send MECHANICS — the optimistic lifecycle every send
 * funnels through, owned by the provider so both the composer (text, media,
 * file, voice, cards, AI) and the message list (failed-bubble retry) reach
 * the same one place:
 *   1. `insertOptimistic` emits the bubble on `willSendMsg$` (`sending`), using
 *      a LOCAL-preview payload for not-yet-uploaded media/audio.
 *   2. `dispatchSend` awaits the upload (if any), persists, then announces the
 *      outcome on `messageSent$` — `sent` (server ids + uploaded payload) or
 *      `failed` (tap to retry).
 * The WS echo dedups by id, so a message never appears twice.
 *
 * This hook is FULLY decoupled from the store and the room handle — it reaches
 * into neither. It talks only through the coordinator bus: `willSendMsg$` to
 * insert the optimistic bubble, `messageSent$` for the outcome (the store
 * reconciles the list, the coordinator materialises the handle), `beforeResend$`
 * to drop a failed bubble before a retry, and a `retryRequested$` subscription
 * for the retry intent. Its only non-bus deps are `conversationHandle` (where + how to
 * send) and the session. `text`, `sending`, the voice recorder, the attachment
 * menu and the AI composer live in `useChatComposer`.
 */

import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { buildQuoteSnapshot } from '@euler/app/flows/chat/utils/buildQuoteSnapshot';
import {
  ChatMediaUploadAsset,
  audioMimeFromUri,
  createChatMediaUploader,
  isRemoteUrl,
  uploadChatAudio,
  uploadChatFile,
} from '@euler/app/flows/chat/utils/uploadChatMedia';
import {
  ChatMediaItem,
  ChatMediasLayout,
  ChatMsg,
  ChatUserMessagePayload,
} from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useEffect } from 'react';
import { ChatConversationHandle, isBaked } from '../handle';
import { ConversationCoordinator } from './useConversationCoordinator';

/** A picked file ready to send (the composer does the picking). */
export interface ChatFileToSend {
  uri: string;
  filename: string;
  mimeType: string;
  sizeInBytes: number;
}

/** A recorded voice message ready to send. */
export interface ChatAudioToSend {
  uri: string;
  mimeType: string;
  durationMs: number;
  waveform: number[];
}

interface ChatSendPipelineDeps {
  /** Where to send + how — bare sends to the peer, baked to the room. */
  conversationHandle: ChatConversationHandle;
  /** The event bus — the pipeline's ONLY channel to the rest of the room. It
   * reads the reply target (`quoteTarget$`), subscribes to the retry intent
   * (`retryRequested$`), and emits the optimistic insert (`willSendMsg$`), the
   * send outcome (`messageSent$`, which the store reconciles from and the
   * coordinator materialises the handle from) and the pre-retry drop
   * (`beforeResend$`). It reaches into NOTHING in the store or the handle. */
  coordinator: ConversationCoordinator;
}

export interface ChatSendPipeline {
  /** Send a READY payload (text / markdown / cards). `quote` attaches the
   * active reply target and clears it. */
  sendPayload: (
    payload: ChatUserMessagePayload,
    opts?: { quote?: boolean },
  ) => Promise<boolean>;
  /**
   * Send picked/pasted image+video assets as ONE message (one → `media`,
   * many → `medias` with the given `layout`).
   */
  sendMediaAssets: (
    assets: ChatMediaUploadAsset[],
    opts?: { layout?: ChatMediasLayout },
  ) => Promise<void>;
  /** Send each asset as its own `media` message ("逐条发送"). */
  sendMediaSeparately: (assets: ChatMediaUploadAsset[]) => Promise<void>;
  sendFile: (file: ChatFileToSend) => Promise<void>;
  sendAudio: (audio: ChatAudioToSend) => Promise<void>;
}

export function useChatSendPipeline(
  deps: ChatSendPipelineDeps,
): ChatSendPipeline {
  const { conversationHandle, coordinator } = deps;
  const { quoteTarget$, willSendMsg$, messageSent$, beforeResend$ } =
    coordinator;

  const { chatService, mediaFileService } = useServiceFactory();
  const { me } = useChatSession();

  // ───────────────────────── central optimistic send

  const insertOptimistic = useCallback(
    (
      payload: ChatUserMessagePayload,
      opts?: {
        quoteMessageId?: string;
        quoteSnapshot?: ChatMsg['quoteSnapshot'];
      },
    ): string => {
      // 32 chars (~190 bits, collision-free) leaves headroom under the
      // server's `client_msg_id` varchar(64); nanoid(64) would fill it
      // exactly and truncate the moment anyone prefixes the key.
      const clientMessageId = nanoid(32);
      willSendMsg$.next({
        kind: 'local',
        clientMessageId,
        status: 'sending',
        // Unknown until a bare room is materialised on reconcile.
        roomId: isBaked(conversationHandle) ? conversationHandle.roomId : '',
        authorId: me.platformUserId,
        authorType: 'staff',
        authorName: me.user.name,
        authorAvatar: me.user.avatar ?? null,
        payloadType: payload.type,
        payload,
        quoteMessageId: opts?.quoteMessageId ?? null,
        quoteSnapshot: opts?.quoteSnapshot ?? null,
        createdAt: new Date().toISOString(),
      });
      return clientMessageId;
    },
    [conversationHandle, me, willSendMsg$],
  );

  // Finalize (optional upload) + persist a pre-inserted optimistic message.
  // `finalize` is the ready payload (text/cards) or a promise resolving to
  // the uploaded one (media/file/audio); `null` = the upload failed.
  const dispatchSend = useCallback(
    async (
      clientMessageId: string,
      finalize: ChatUserMessagePayload | Promise<ChatUserMessagePayload | null>,
      quoteMessageId?: string,
    ): Promise<boolean> => {
      let finalPayload: ChatUserMessagePayload | null = null;
      try {
        finalPayload = await finalize;
        if (!finalPayload) {
          messageSent$.next({ status: 'failed', clientMessageId });
          return false;
        }
        const payload = finalPayload;
        const result = isBaked(conversationHandle)
          ? conversationHandle.roomType === 'group'
            ? await chatService.sendGroupMessage({
                roomId: conversationHandle.roomId,
                payload,
                clientMessageId,
                quoteMessageId,
              })
            : await chatService.sendPrivateMessage({
                roomId: conversationHandle.roomId,
                payload,
                clientMessageId,
                quoteMessageId,
              })
          : await chatService.sendPrivateMessage({
              to: {
                platformUserId: conversationHandle.peer.platformUserId,
                appId: conversationHandle.peer.appId,
                appScope: conversationHandle.peer.appScope,
              },
              payload,
              clientMessageId,
              quoteMessageId,
            });
        if (!result) {
          messageSent$.next({
            status: 'failed',
            clientMessageId,
            finalPayload: payload,
          });
          return false;
        }

        // The outcome carries the result's ids; the coordinator materialises
        // the room handle off this when a first send creates the room/thread,
        // and the store reconciles the optimistic bubble — the pipeline owns
        // neither concern.
        messageSent$.next({
          status: 'sent',
          clientMessageId,
          messageId: result.messageId,
          roomId: result.roomId,
          threadId: result.threadId ?? null,
          payload,
        });
        return true;
      } catch (e) {
        console.error('send message failed', e);
        messageSent$.next({
          status: 'failed',
          clientMessageId,
          finalPayload: finalPayload ?? undefined,
        });
        return false;
      }
    },
    [conversationHandle, chatService, messageSent$],
  );

  // Upload only the still-local items of a `medias` album, preserving any
  // already-hosted items in place. `allSettled` so one failed item doesn't
  // discard the others' progress; `complete` is false when any item is still
  // local after the pass. Position-preserving so the album order is kept.
  const uploadMediasAlbum = useCallback(
    async (
      items: ChatMediaItem[],
    ): Promise<{ items: ChatMediaItem[]; complete: boolean }> => {
      const uploader = createChatMediaUploader(mediaFileService);
      try {
        const settled = await Promise.allSettled(
          items.map(it =>
            isRemoteUrl(it.url)
              ? Promise.resolve(it)
              : uploader.upload({
                  kind: it.kind,
                  uri: it.url,
                  mimeType: it.kind === 'video' ? 'video/mp4' : 'image/jpeg',
                }),
          ),
        );
        let complete = true;
        const merged = settled.map((r, i) => {
          if (r.status === 'fulfilled') return r.value;
          console.error('chat medias item upload failed', r.reason);
          complete = false;
          return items[i]; // keep the local item for a later retry
        });
        return { items: merged, complete };
      } finally {
        uploader.dispose();
      }
    },
    [mediaFileService],
  );

  // Send a `medias` album: upload the stragglers, then send if every item is
  // hosted, else mark failed but keep the partly-uploaded album on the bubble
  // so a retry only re-uploads what's still local (never re-sends a file://,
  // and never re-uploads an already-hosted item). `layout` is the sender's
  // chosen bubble style, carried with the message.
  const dispatchAlbum = useCallback(
    async (
      clientMessageId: string,
      items: ChatMediaItem[],
      layout: ChatMediasLayout | undefined,
    ): Promise<boolean> => {
      const { items: merged, complete } = await uploadMediasAlbum(items);
      const album: ChatUserMessagePayload = {
        type: 'medias',
        items: merged,
        layout,
      };
      if (!complete) {
        messageSent$.next({
          status: 'failed',
          clientMessageId,
          finalPayload: album,
        });
        return false;
      }
      return await dispatchSend(clientMessageId, album);
    },
    [uploadMediasAlbum, dispatchSend, messageSent$],
  );

  const sendPayload = useCallback(
    async (
      payload: ChatUserMessagePayload,
      opts?: { quote?: boolean },
    ): Promise<boolean> => {
      const useQuote = opts?.quote ?? false;
      const quoteTarget = quoteTarget$.getValue();
      // Only a confirmed (remote) message can be the server-side quote target.
      const quoteMessageId =
        useQuote && quoteTarget?.kind === 'remote'
          ? quoteTarget.messageId
          : undefined;
      const quoteSnapshot =
        useQuote && quoteTarget ? buildQuoteSnapshot(quoteTarget) : null;
      const clientMessageId = insertOptimistic(payload, {
        quoteMessageId,
        quoteSnapshot,
      });
      if (useQuote) quoteTarget$.next(null);
      return await dispatchSend(clientMessageId, payload, quoteMessageId);
    },
    [insertOptimistic, dispatchSend, quoteTarget$],
  );

  // Re-upload local (file://) assets before a retry — re-sending the payload
  // as-is would persist an unreachable device path. Returns the finalize
  // promise when a re-upload is needed, or null when the payload is already
  // remote / has no asset (text, cards, already-uploaded media).
  const reuploadLocalAssets = useCallback(
    (
      payload: ChatUserMessagePayload,
    ): Promise<ChatUserMessagePayload | null> | null => {
      switch (payload.type) {
        case 'audio':
          if (isRemoteUrl(payload.url)) return null;
          return uploadChatAudio(mediaFileService, {
            uri: payload.url,
            mimeType: audioMimeFromUri(payload.url),
            durationMs: payload.durationMs,
            waveform: payload.waveform,
          }).catch(e => {
            console.error('voice message re-upload failed', e);
            return null;
          });
        case 'file':
          if (isRemoteUrl(payload.url)) return null;
          return uploadChatFile(mediaFileService, {
            uri: payload.url,
            filename: payload.filename,
            mimeType: payload.mimeType,
            sizeInBytes: payload.sizeInBytes,
          }).catch(e => {
            console.error('chat file re-upload failed', e);
            return null;
          });
        case 'media': {
          if (isRemoteUrl(payload.url)) return null;
          const uploader = createChatMediaUploader(mediaFileService);
          return uploader
            .upload({
              kind: payload.kind,
              uri: payload.url,
              mimeType: payload.kind === 'video' ? 'video/mp4' : 'image/jpeg',
            })
            .then(
              item => ({ type: 'media', ...item }) as ChatUserMessagePayload,
            )
            .catch(e => {
              console.error('chat media re-upload failed', e);
              return null;
            })
            .finally(() => uploader.dispose());
        }
        // `medias` is handled by `dispatchAlbum` (partial-upload carry), not
        // here — `retry` routes it there before reaching this helper.
        default:
          return null;
      }
    },
    [mediaFileService],
  );

  const retry = useCallback(
    (msg: ChatMsg) => {
      // Only a local (failed) message is retryable.
      if (msg.kind !== 'local') return;
      // Announce the resend so the store drops the failed bubble; a fresh
      // optimistic one is then inserted below.
      beforeResend$.next(msg.clientMessageId);
      // A local (sendable) message always carries a user payload, never a
      // notification — narrow to what the send path accepts.
      const payload = msg.payload as ChatUserMessagePayload;
      // An album with any still-local item re-uploads only the stragglers via
      // the album path; an all-hosted album falls through to a plain re-send.
      if (
        payload.type === 'medias' &&
        payload.items.some(it => !isRemoteUrl(it.url))
      ) {
        const clientMessageId = insertOptimistic(payload);
        void dispatchAlbum(clientMessageId, payload.items, payload.layout);
        return;
      }
      const finalize = reuploadLocalAssets(payload);
      if (finalize) {
        const clientMessageId = insertOptimistic(payload);
        void dispatchSend(clientMessageId, finalize);
      } else {
        void sendPayload(payload);
      }
    },
    [
      sendPayload,
      reuploadLocalAssets,
      insertOptimistic,
      dispatchSend,
      dispatchAlbum,
      beforeResend$,
    ],
  );

  // The bubble's retry intent is routed through the coordinator bus.
  useEffect(() => {
    const sub = coordinator.retryRequested$.subscribe(retry);
    return () => sub.unsubscribe();
  }, [coordinator.retryRequested$, retry]);

  // Send a single image/video asset as a `media` message.
  const sendOneMedia = useCallback(
    async (asset: ChatMediaUploadAsset) => {
      const clientMessageId = insertOptimistic({
        type: 'media',
        kind: asset.kind,
        url: asset.uri,
      });
      const uploader = createChatMediaUploader(mediaFileService);
      try {
        await dispatchSend(
          clientMessageId,
          uploader
            .upload(asset)
            .then(
              item => ({ type: 'media', ...item }) as ChatUserMessagePayload,
            )
            .catch(e => {
              console.error('chat media upload failed', e);
              return null;
            }),
        );
      } finally {
        uploader.dispose();
      }
    },
    [mediaFileService, insertOptimistic, dispatchSend],
  );

  // Send image/video assets as ONE message: a single asset sends a `media`
  // message; multiple bundle into ONE `medias` album with the given `layout`.
  // Bubbles show INSTANTLY from local URIs; uploads run in parallel and swap
  // to hosted URL(s) on reconcile. Shared by the picker, camera, and paste.
  const sendMediaAssets = useCallback(
    async (
      assets: ChatMediaUploadAsset[],
      opts?: { layout?: ChatMediasLayout },
    ) => {
      if (!assets.length) return;
      if (assets.length === 1) {
        await sendOneMedia(assets[0]);
        return;
      }
      // Album: upload via the partial-carry path so one failed item doesn't
      // discard the rest — a retry then re-uploads only the stragglers.
      const items: ChatMediaItem[] = assets.map(a => ({
        kind: a.kind,
        url: a.uri,
      }));
      const clientMessageId = insertOptimistic({
        type: 'medias',
        items,
        layout: opts?.layout,
      });
      await dispatchAlbum(clientMessageId, items, opts?.layout);
    },
    [insertOptimistic, dispatchAlbum, sendOneMedia],
  );

  // Send each asset as its OWN `media` message ("逐条发送"). Bubbles insert
  // optimistically in pick order; uploads run in parallel.
  const sendMediaSeparately = useCallback(
    async (assets: ChatMediaUploadAsset[]) => {
      await Promise.all(assets.map(asset => sendOneMedia(asset)));
    },
    [sendOneMedia],
  );

  const sendFile = useCallback(
    async (file: ChatFileToSend) => {
      const clientMessageId = insertOptimistic({
        type: 'file',
        url: file.uri,
        filename: file.filename,
        mimeType: file.mimeType,
        sizeInBytes: file.sizeInBytes,
      });
      await dispatchSend(
        clientMessageId,
        uploadChatFile(mediaFileService, file).catch(e => {
          console.error('chat file upload failed', e);
          return null;
        }),
      );
    },
    [mediaFileService, insertOptimistic, dispatchSend],
  );

  const sendAudio = useCallback(
    async (audio: ChatAudioToSend) => {
      // Optimistic voice bubble plays from the local recording while the
      // upload runs; it swaps to the hosted URL once the send reconciles.
      const clientMessageId = insertOptimistic({
        type: 'audio',
        url: audio.uri,
        durationMs: audio.durationMs,
        waveform: audio.waveform,
      });
      await dispatchSend(
        clientMessageId,
        uploadChatAudio(mediaFileService, audio).catch(e => {
          console.error('voice message upload failed', e);
          return null;
        }),
      );
    },
    [mediaFileService, insertOptimistic, dispatchSend],
  );

  return {
    sendPayload,
    sendMediaAssets,
    sendMediaSeparately,
    sendFile,
    sendAudio,
  };
}
